/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

/**
 * English (fallback) strings. Keys are grouped by surface. Tamil and Hindi
 * mirror this shape; a missing key falls back to English at runtime.
 */
const THERAPY_PLANS = 'Therapy Plans';
const HOME_TASKS = 'Home Tasks';
const SOAP_NOTES = 'SOAP Notes';
const SELECT_A_CLIENT = 'Select a client';
const FILTER_BY_STATUS = 'Filter by status';
const ALL_STATUS = 'All Status';
const ACTIVE_CLIENTS = 'Active Patients';
const MY_CLIENTS = 'My Patients';
const SESSIONS_UNIT = '{{count}} sessions';
const NO_DATA_YET = 'No data yet.';
const UPCOMING_SESSIONS = 'Upcoming Sessions';
const LAST_6_MONTHS = 'Last 6 months';

export default {
  common: {
    appName: 'TherapyOS',
    loading: 'Loading…',
    save: 'Save Changes',
    cancel: 'Cancel',
    previous: 'Previous',
    next: 'Next',
    signOut: 'Logout',
    search: 'Search…',
    notFound: 'Page not found',
    backHome: 'Back to Home',
    loadError: 'Could not load data. Please try again.',
    saveError: 'Could not save. Please check the form and try again.',
    view: 'View',
    edit: 'Edit',
    delete: 'Delete',
  },
  topbar: {
    back: 'Go back',
    welcome: 'Welcome back',
    welcomeName: 'Welcome back, {{name}}',
    addNew: 'Add New',
    notifications: 'Notifications',
    noNotifications: 'No new notifications',
    toggleTheme: 'Toggle dark mode',
  },
  status: {
    active: 'Active',
    inactive: 'Inactive',
    discharged: 'Discharged',
    scheduled: 'Scheduled',
    in_progress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
    no_show: 'No-show',
    client_late: 'Client Late',
    consultant_attended_client_absent: 'Client Absent',
  },
  goalStatus: {
    not_started: 'Not Started',
    emerging: 'Emerging',
    in_progress: 'In Progress',
    achieved: 'Achieved',
    maintenance: 'Maintenance',
    discontinued: 'Discontinued',
  },
  measurementType: {
    frequency: 'Frequency',
    percent_accuracy: 'Percent accuracy',
    duration: 'Duration',
    latency: 'Latency',
    trials_to_criterion: 'Trials to criterion',
    rating_scale: 'Rating scale',
    independence_level: 'Independence level',
    prompt_level: 'Prompt level',
    anecdotal: 'Anecdotal',
  },
  sessionType: {
    individual: 'Individual',
    group: 'Group',
    teletherapy: 'Teletherapy',
    assessment: 'Assessment',
    review: 'Review',
    supervision: 'Supervision',
    home_visit: 'Home Visit',
  },
  therapist: {
    dashboardHome: {
      title: 'My Dashboard',
      newSession: 'New Session',
      totalClients: 'Total Patients',
      sessionsToday: 'Sessions Today',
      sessionsCompleted: '{{count}} completed',
      pendingNotes: 'Pending Notes',
      dueToday: 'due today',
      avgProgress: 'Avg Progress',
      caseloadAvg: 'caseload average',
      todaySchedule: "Today's Schedule",
      scheduledCount: '{{count}} sessions scheduled',
      noSchedule: 'No sessions scheduled today.',
      pendingTasks: 'Pending Tasks',
      tasksRemaining: '{{count}} tasks remaining',
      noTasks: 'All caught up — no pending notes.',
      completeNoteFor: 'Complete session notes for {{name}}',
      progressTrend: 'Average Patient Progress',
      progressTrendSub: 'Weekly sessions completed (last 6 weeks)',
      weekLabel: 'W{{n}}',
      myClients: MY_CLIENTS,
      activeCaseload: 'Active caseload',
      viewAll: 'View All',
      noClients: 'No patients assigned yet.',
      nextSession: 'Next: {{when}}',
      noUpcoming: 'No upcoming session',
      sessionsUnit: SESSIONS_UNIT,
    },
    dashboard: {
      myClients: MY_CLIENTS,
      upcoming: UPCOMING_SESSIONS,
      plans: THERAPY_PLANS,
    },
    intake: {
      title: 'Intake Assessment',
      subtitle: 'Everything the family submitted during intake, plus the clinic report.',
      empty: 'No intake on record for this patient.',
    },
    clients: {
      title: MY_CLIENTS,
      count: '{{count}} patients',
      searchPlaceholder: 'Search patients…',
      empty: 'No patients assigned yet.',
      newSession: 'New Session',
      ageSessions: 'Age {{age}} • {{count}} sessions',
      sessionsOnly: SESSIONS_UNIT,
      next: 'Next: {{when}}',
      noUpcoming: 'No upcoming session',
      profileTitle: 'Patient Details',
      sessions: 'Sessions',
      progress: 'Progress',
      status: 'Status',
      nextSession: 'Next Session',
      nextLabel: 'Next',
      guardian: 'Guardian',
      phone: 'Phone',
      therapist: 'Therapist',
      plan: 'Plan',
      sessionsPerWeek: 'Sessions/wk',
      feePerSession: 'Fee/session',
      viewIntake: 'View Intake & Report',
      backToList: 'Back to My Patients',
      homeTasks: 'Home Tasks',
      col: {
        patient: 'Patient',
        age: 'Age',
        diagnosis: 'Diagnosis',
        sessions: 'Sessions',
        progress: 'Progress',
        next: 'Next',
        status: 'Status',
      },
    },
    notes: {
      title: SOAP_NOTES,
      subtitle: 'Document your sessions',
      count: '{{count}} sessions to document',
      empty: 'No sessions to document yet.',
      open: 'Open Note',
      newNote: 'New Note',
      newNoteTitle: 'New Session Note',
      newNotePrompt: 'Choose a session to document',
      noDocumentable: 'No started sessions are available to document yet.',
    },
    schedule: {
      title: 'Schedule',
      subtitle: 'Your weekly session calendar',
      add: 'New Session',
      empty: 'No upcoming sessions.',
      noneDay: 'No sessions',
      noSessionsDay: 'No sessions scheduled for this day.',
      prevWeek: 'Previous week',
      nextWeek: 'Next week',
      thisWeek: 'This week',
      weekOf: 'Week of {{range}}',
      daySessions: SESSIONS_UNIT,
      minutes: '{{n}} min',
      start: 'Start',
      active: 'Active',
      done: 'Done',
    },
    availability: {
      title: 'Availability',
      subtitle: 'Your recurring weekly working hours',
      save: 'Save Availability',
      saved: 'Availability saved.',
      saveError: 'Could not save availability. Please try again.',
      invalid: 'Check your time windows — end must be after start and windows must not overlap.',
      addWindow: 'Add window',
      removeWindow: 'Remove window',
      none: 'Not available',
      days: {
        mon: 'Monday',
        tue: 'Tuesday',
        wed: 'Wednesday',
        thu: 'Thursday',
        fri: 'Friday',
        sat: 'Saturday',
        sun: 'Sunday',
      },
    },
    sessions: {
      title: 'Sessions',
      subtitle: "Today's sessions and lifecycle",
      empty: 'No sessions scheduled.',
      colClient: 'Patient',
      colType: 'Type',
      colTime: 'Time',
      colDuration: 'Duration',
      colRoom: 'Room',
      colStatus: 'Status',
      colActions: 'Actions',
      detailsTitle: 'Session Details',
      start: 'Start',
      complete: 'Complete',
      cancel: 'Cancel',
      reschedule: 'Reschedule',
      note: 'Session Note',
      rate: 'Rate',
      rescheduleTitle: 'Reschedule Session',
      rescheduleSubtitle:
        'Pick a new time. Clashes with the therapist’s other sessions are rejected.',
    },
    rate: {
      title: 'Rate session',
      subtitle: 'Engagement, cooperation and context',
      engagement: 'Engagement',
      cooperation: 'Cooperation',
      note: 'Context note',
      unrated: 'Not rated',
      submit: 'Save rating',
    },
    note: {
      title: 'Session Note',
      back: 'Back to Sessions',
      signed: 'Signed',
      trials: 'Goal Trial Data',
      correct: 'Correct',
      incorrect: 'Incorrect',
      prompted: 'Prompted',
      subjective: 'Subjective',
      objective: 'Objective',
      assessment: 'Assessment',
      plan: 'Plan',
      saveDraft: 'Save Draft',
      sign: 'Sign & Lock',
    },
    plans: {
      title: THERAPY_PLANS,
      subtitle: 'Individualised therapy plans for your patients',
      count: '{{count}} active plans',
      searchPlaceholder: 'Search plans by name…',
      empty: 'No therapy plans yet.',
      progress: 'Overall Progress',
      review: 'Review',
      reviewDate: 'Review Date',
      currentGoals: 'Current Goals',
      moreGoals: '+{{count}} more',
      viewDetails: 'View Details',
      updateProgress: 'Update Progress',
      detailsTitle: 'Therapy Plan Details',
      client: 'Patient',
      program: 'Program',
      startDate: 'Start Date',
      goals: 'Goals',
    },
    planForm: {
      newPlan: 'New Plan',
      subtitle: 'Build a therapy plan and its goals for a client.',
      fromTemplate: 'Start from a template',
      fromTemplateNone: 'No template — start blank',
      client: 'Patient',
      selectClient: SELECT_A_CLIENT,
      name: 'Plan name',
      programType: 'Program type',
      startDate: 'Start date',
      reviewDate: 'Review date',
      goals: 'Goals',
      addGoal: 'Add goal',
      goalN: 'Goal {{n}}',
      goalTitle: 'Goal',
      domain: 'Domain',
      selectDomain: 'Select domain',
      measurementType: 'Measurement type',
      selectMeasurementType: 'Select method',
      measure: 'Measurement detail',
      baseline: 'Baseline',
      target: 'Target',
      goalReviewDate: 'Goal review date',
      approachTags: 'Approach tags',
      approachTagsHint: 'Comma-separated, e.g. PECS, DTT',
      subTargets: 'Sub-targets',
      subTargetsHint: 'Separate with ; — add steps after >, e.g. Imitate > model, prompt',
      fromLibrary: 'From library',
      allDomains: 'All domains',
      pickDomain: 'Filter by domain',
      pickGoal: 'Pick a goal…',
      domains: {
        SLT: 'Speech & Language',
        OT: 'Occupational Therapy',
        ABA: 'Applied Behaviour',
        PT: 'Physiotherapy',
        SPECED: 'Special Education',
      },
      removeGoal: 'Remove goal',
      submit: 'Create plan',
    },
    plan: {
      back: 'Back to Plans',
      goals: 'Current Goals',
      noGoals: 'This plan has no goals yet.',
      updateStatus: 'Update goal status',
      subTargets: 'Sub-targets',
      reviewBy: 'Review by {{date}}',
      statusNote: 'Status-change note (optional)',
      statusNotePlaceholder: 'Why is the status changing?',
      history: 'Status history',
      showHistory: 'Status history',
      hideHistory: 'Hide history',
      noHistory: 'No status changes recorded yet.',
      historyEntry: '{{from}} → {{to}}',
      historyInitial: 'Set to {{to}}',
      saveAsTemplate: 'Save as template',
      exportPdf: 'Export PDF',
      exporting: 'Exporting…',
      saveTemplateTitle: 'Save as Template',
      saveTemplateSubtitle: 'Reuse this plan’s goals when creating future plans.',
      templateName: 'Template name',
    },
    templates: {
      title: 'Templates',
      subtitle: 'Reusable plan goal sets for your clinic',
      empty: 'No templates yet. Save a plan as a template to reuse its goals.',
      goalCount: '{{count}} goals',
      delete: 'Delete template',
    },
    collaborators: {
      title: 'Collaborating Therapists',
      none: 'No collaborators yet.',
      add: 'Add collaborator…',
      remove: 'Remove collaborator',
    },
    review: {
      title: 'Clinical Reviews',
      due: 'Review due',
      dueBanner: '{{count}} plan(s) due for clinical review',
      record: 'Record review',
      empty: 'No reviews recorded yet.',
      modalTitle: 'Record clinical review',
      modalSubtitle: 'Capture the review outcome and set the next review date.',
      outcome: 'Outcome',
      outcome_continue: 'Continue plan',
      outcome_revise: 'Revise plan',
      outcome_discharge: 'Discharge',
      summary: 'Summary',
      recommendations: 'Recommendations (optional)',
      nextDate: 'Next review date',
      submit: 'Save review',
    },
    homeTasks: {
      link: HOME_TASKS,
      back: 'Back to My Patients',
      title: HOME_TASKS,
      subtitle: 'Assign and review home-programme tasks',
    },
  },
  parent: {
    selectChild: 'Child',
    noData: 'No data recorded yet',
    patientDetails: {
      title: 'Patient Details',
      subtitle: "Complete your child's intake assessment for the clinic",
    },
    dashboard: {
      title: 'Welcome',
      subtitle: "Your children's therapy at a glance",
      empty: 'No children are enrolled yet.',
      progress: 'Overall Progress',
      viewProgress: 'View progress →',
      recentReports: 'Recent Reports',
    },
    dashboardHome: {
      title: "{{name}}'s Dashboard",
      subtitle: "Track {{name}}'s therapy journey",
      bookSession: 'Book Session',
      years: '{{count}} years',
      today: 'Today',
      nextSession: 'Next Session',
      none: 'None',
      session: 'Session',
      progress: 'Progress',
      goalsOnTrack: 'Goals on track',
      pendingDues: 'Pending Dues',
      allPaid: 'All paid',
      due: 'Outstanding',
      upcoming: 'Upcoming Sessions',
      overallProgress: 'Overall Progress',
      sessionsCompleted: 'Sessions Completed',
      upcomingSessions: UPCOMING_SESSIONS,
      achievements: 'Achievements',
      thisWeek: '{{count}} this week',
      nextLabel: 'Next: {{when}}',
      newThisMonth: '{{count}} new',
      progressOverTime: 'Progress Over Time',
      progressOverTimeSub: 'Goal accuracy over recent sessions',
      homeTasks: 'Home Tasks',
      dailyActivities: 'Daily activities',
      noTasks: 'No home tasks assigned yet.',
      yourSchedule: 'Your schedule',
      viewCalendar: 'View Full Calendar',
      noUpcoming: 'No upcoming sessions.',
      milestones: 'Milestones reached',
      noAchievements: 'No milestones yet — keep going!',
      dayStreak: '{{count}}-day streak',
      viewAll: 'View All',
      noReports: 'No reports yet.',
    },
    achievements: {
      dayStreak: '{{count}}-day streak',
      completed: '{{count}} done',
      badge: {
        firstStep: 'First step',
        threeDay: '3-day streak',
        weekWarrior: 'Week warrior',
        fiftyDone: '50 completed',
      },
    },
    therapyPlan: {
      title: 'Therapy Plan',
      subtitle: 'Goals and progress for your child',
      target: 'Target: {{date}}',
    },
    progress: {
      title: 'Progress Tracking',
      subtitle: "Track your child's therapy journey",
      empty: 'No progress data has been recorded yet.',
      percentComplete: '{{pct}}% complete',
      milestones: 'Milestones Achieved',
      milestonesSub: 'Goals reached and sessions completed',
      noMilestones: 'No milestones recorded yet.',
      goalProgress: 'Goal Progress',
      goalProgressSub: 'Accuracy over time, per goal',
      kind: {
        goal_achieved: 'Goal achieved',
        session_completed: 'Session completed',
        plan_version: 'Plan updated',
      },
    },
    sessions: {
      title: 'Session History',
      subtitle: "Your child's past and upcoming sessions",
      session: 'Session',
      empty: 'No sessions scheduled.',
      colChild: 'Child',
      colType: 'Type',
      colTime: 'Time',
      colStatus: 'Status',
      bookSession: 'Book Session',
      upcoming: UPCOMING_SESSIONS,
      past: 'Past Sessions',
      date: 'Date',
      time: 'Time',
      type: 'Type',
      noUpcoming: 'No upcoming sessions.',
      noPast: 'No past sessions.',
    },
    billing: {
      title: 'Billing',
      subtitle: 'Your invoices and payments',
      empty: 'No invoices yet.',
      tab_proformas: 'Proformas',
      tab_invoices: 'Invoices',
      due: 'Due: {{date}}',
      heading: 'Billing & Payments',
      headingSub: 'Invoice history and payment management',
      totalPaid: 'Total Paid',
      outstanding: 'Outstanding',
      totalSessions: 'Total Sessions',
      outstandingAlert: 'You have an outstanding balance of {{amount}}. Please make payment soon.',
      payNow: 'Pay now',
      payNowTitle: 'Pay invoice',
      payNowSubtitle: 'Pay the outstanding balance securely (Razorpay).',
      payConfirm: 'Pay now',
      paying: 'Processing…',
      payError: 'Payment could not be processed. Please try again.',
    },
    reports: {
      title: 'Reports',
      count: '{{count}} reports',
      empty: 'No reports shared with you yet.',
    },
    homeTasks: {
      title: HOME_TASKS,
      subtitle: "Today's therapy practice at home",
      dailyProgress: 'Daily Progress',
      completedCount: '{{done}} of {{total}} completed today',
      completed: 'Completed ✓',
      dueToday: 'Due today',
      markDone: 'Mark as done',
      empty: 'No home tasks assigned yet.',
    },
  },
  admin: {
    dashboard: {
      activeClients: ACTIVE_CLIENTS,
      therapists: 'Therapists',
      sessions: 'Sessions',
      revenue: 'Revenue',
      sessionsThisMonth: 'Sessions This Month',
      totalRevenue: 'Total Revenue',
      avgSatisfaction: 'Avg Satisfaction',
      todaySessions: "Today's Sessions",
      therapistSplit: '{{onRoll}} On-Roll · {{consultant}} Consultant',
      todayBreakdown: '{{completed}} completed · {{inProgress}} in progress',
      overdue: '{{amount}} overdue',
      avgProgress: 'Avg Plan Progress',
      plans: THERAPY_PLANS,
      distribution: 'Patient Distribution',
      distributionSub: 'By therapy type',
      revenueSessions: 'Revenue & Sessions',
      last6Months: LAST_6_MONTHS,
      sessionsTrend: 'Sessions',
      sessionsTrendSub: LAST_6_MONTHS,
      performance: 'Therapist Performance',
      performanceSub: 'Top performers',
      sessionsUnit: SESSIONS_UNIT,
      satisfaction: '{{pct}}% satisfaction',
      upcoming: UPCOMING_SESSIONS,
      upcomingSub: "Today's schedule",
      noUpcoming: 'No sessions scheduled today.',
      noData: NO_DATA_YET,
      viewAll: 'View All',
      noActivity: 'No recent activity.',
      activity: {
        title: 'Recent Activities',
        sub: 'Latest system events',
        system: 'System',
        justNow: 'just now',
        minutesAgo: '{{count}} min ago',
        hoursAgo: '{{count}} hour ago',
        daysAgo: '{{count}} day ago',
        clientCreated: 'added a new client',
        noteSigned: 'signed a session note',
        planVersion: 'updated a therapy plan',
        reviewRecorded: 'recorded a clinical review',
        collaboratorAdded: 'added a plan collaborator',
        invoiceCreated: 'created an invoice',
        invoicePaid: 'recorded a payment',
        payrollProcessed: 'processed payroll',
        userInvited: 'invited a team member',
        userActivated: 'activated an account',
        reportGenerated: 'generated a report',
      },
    },
    clients: {
      title: 'Patients',
      subtitle: 'Children enrolled at your clinic',
      count: '{{count}} patients total',
      add: 'Add Patient',
      edit: 'Edit Patient',
      addSubtitle: 'Create a new patient record',
      empty: 'No patients yet. Add your first patient to get started.',
      searchPlaceholder: 'Search patients, diagnosis, therapist…',
      statusFilter: FILTER_BY_STATUS,
      allStatus: ALL_STATUS,
      age: 'Age {{age}}',
      colName: 'Patient',
      colDiagnosis: 'Diagnosis',
      colTherapist: 'Therapist',
      colSessions: 'Sessions',
      colProgress: 'Progress',
      colStatus: 'Status',
      colIntake: 'Intake',
      colActions: 'Actions',
      deleteTitle: 'Delete patient?',
      deleteBody: 'Permanently delete {{name}} and all their records. This cannot be undone.',
    },
    clientDetail: {
      back: 'Back to Patients',
      notFound: 'Patient not found.',
      loadError: 'This patient could not be loaded — they may have been removed.',
      age: 'Age',
      therapist: 'Therapist',
      assignTherapist: 'Assign',
      changeTherapist: 'Change',
      assignTitle: 'Assign therapist',
      assignSubtitle: 'Choose the therapist responsible for this patient.',
      assignAction: 'Assign',
      guardian: 'Guardian',
      contact: 'Contact',
      plans: 'Therapy Plans',
      noPlans: 'No therapy plans yet.',
      sessions: 'Recent Sessions',
      noSessions: 'No sessions yet.',
      billing: 'Billing',
      noInvoices: 'No invoices yet.',
      intake: 'Patient Intake',
    },
    sessions: {
      title: 'Schedule',
      count: SESSIONS_UNIT,
      empty: 'No sessions scheduled.',
      searchPlaceholder: 'Search by type or location…',
      statusFilter: FILTER_BY_STATUS,
      allStatus: ALL_STATUS,
      schedule: 'Add Session',
      scheduleSubtitle: 'Book a new therapy session',
      colClient: 'Patient',
      colTherapist: 'Therapist',
      colType: 'Type',
      colRoom: 'Room',
      viewList: 'List',
      viewMonth: 'Month',
      view_list: 'List',
      view_week: 'Week',
      view_day: 'Day',
      view_month: 'Month',
      today: 'Today',
      moreCount: '+{{count}} more',
      filterTherapist: 'Filter by therapist',
      allTherapists: 'All therapists',
      colTime: 'Time',
      colDateTime: 'Date & Time',
      colStatus: 'Status',
      durationMin: '{{count}}min',
      cancelSession: 'Cancel session',
      cancelTitle: 'Cancel this session?',
      cancelBody: 'The session will be marked cancelled. This cannot be undone.',
      cancelConfirm: 'Cancel session',
      setOutcome: 'Set outcome',
      outcomeTitle: 'Record session outcome',
      outcomeBody: 'Mark what happened in this session.',
      outcomeComplete: 'Completed',
      outcomeClientLate: 'Client came late',
      outcomeNoShow: 'No-show',
      consultantAbsentTitle: 'Consultant attended, client absent (30% pay)',
      checkInHint: 'Mark the consultant as arrived to enable the 30% absent rule.',
      markConsultantArrived: 'Mark consultant arrived',
      absentReason: 'Reason (required)',
      markClientAbsent: 'Mark client absent (30%)',
    },
    users: {
      title: 'Team Members',
      pageTitle: 'Users & Roles',
      pageSubtitle: 'Manage your clinic team and their access',
      tabUsers: 'Users',
      tabRoles: 'Roles & Permissions',
      count: '{{count}} users total',
      invite: 'Invite User',
      inviteTitle: 'Invite a team member',
      inviteSubtitle: 'They will receive an email to set their password.',
      empty: 'No team members yet. Invite your first user to get started.',
      enable: 'Enable',
      disable: 'Disable',
      name: 'Full Name',
      email: 'Email',
      role: 'Role',
      inviteError: 'Could not send the invite. Please try again.',
      col: {
        name: 'Name',
        email: 'Email',
        roles: 'Roles',
        status: 'Status',
        lastLogin: 'Last Login',
      },
      role_CLINIC_ADMIN: 'Clinic Admin',
      role_THERAPIST: 'Therapist',
      role_FINANCE: 'Finance',
      status_invited: 'Invited',
      status_active: 'Active',
      status_disabled: 'Disabled',
    },
    therapists: {
      title: 'Therapist Management',
      subtitle: 'Clinical staff at your clinic',
      registered: '{{count}} therapists registered',
      add: 'Add Therapist',
      addSubtitle: 'Create a new therapist profile',
      edit: 'Edit Therapist',
      editSubtitle: 'Update this therapist profile',
      empty: 'No therapists yet. Add your first therapist to get started.',
      searchPlaceholder: 'Search therapists…',
      statusFilter: FILTER_BY_STATUS,
      allStatus: ALL_STATUS,
      clients: 'Patients',
      sessions: 'Sessions',
      rating: 'Rating',
      joined: 'Joined {{date}}',
      tab: { all: 'All' },
      perMonth: '/mo',
      perSession: '/session',
      col: {
        name: 'Name',
        type: 'Type',
        specialty: 'Specialty',
        sessions: 'Sessions',
        status: 'Status',
        pay: 'Pay',
      },
    },
    therapistProfile: {
      title: 'Therapist Profile',
      activeClients: ACTIVE_CLIENTS,
      totalSessions: 'Total Sessions',
      joinDate: 'Join Date',
      status: 'Status',
    },
    finance: {
      title: 'Finance',
      allInvoices: 'All Invoices',
      count: '{{count}} invoices',
    },
    financeOverview: {
      title: 'Finance Overview',
      subtitle: '{{month}} financial summary',
      totalRevenue: 'Total Revenue',
      outstanding: 'Outstanding',
      processedToday: 'Processed Today',
      netProfit: 'Net Profit',
      outstandingInvoices: 'Outstanding Invoices',
      noOutstanding: 'No outstanding invoices. 🎉',
      due: 'Due {{date}}',
      sendReminder: 'Send Reminder',
      reminderSent: 'Reminder sent',
    },
    reports: {
      title: 'Reports',
      count: '{{count}} reports',
    },
    invite: {
      title: 'Invite Parent',
      subtitle: 'Create a parent login for this child',
      intro: "Send {{child}}'s parent an invite to access the Patient portal.",
      parentName: "Parent's full name",
      email: "Parent's email",
      submit: 'Send invite',
      error: 'Could not send the invite. The email may already be in use.',
      success: 'Invite emailed to {{email}}.',
      linkLabel: 'Activation link',
      copy: 'Copy',
      copied: 'Copied',
      note: 'We emailed this link to the parent. You can also copy and share it — it lets them set a password and sign in, and expires in 7 days.',
    },
  },
  form: {
    client: {
      fullName: 'Full Name',
      age: 'Age',
      primaryDiagnosis: 'Diagnosis',
      selectDiagnosis: 'Select diagnosis',
      email: 'Email',
      phone: 'Phone',
      assignTherapist: 'Assigned Therapist',
      noTherapist: 'No therapist assigned',
      submit: 'Add Patient',
      guardianName: 'Parent / Guardian Name',
      sessionsPerWeek: 'Sessions / week',
      inviteParentNow: 'Also create a parent login',
      inviteParentHint: "We'll email the parent an activation link to access the Patient portal.",
      parentName: "Parent's full name",
      parentEmail: 'Parent Email',
    },
    therapist: {
      fullName: 'Full Name',
      email: 'Email',
      phone: 'Phone',
      specialty: 'Specialty',
      selectSpecialty: 'Select a specialty',
      specialisations: 'Specialisations',
      specialisationsHint: 'Comma-separated, e.g. ASD, Speech, OT',
      joinedOn: 'Joined On',
      category: 'Engagement',
      categoryOption: { on_roll: 'On-Roll', consultant: 'Consultant' },
      perSessionRate: 'Per-session rate (₹)',
      monthlyFixed: 'Monthly base (₹)',
      submit: 'Add Therapist',
    },
    session: {
      client: 'Patient',
      selectClient: SELECT_A_CLIENT,
      therapist: 'Therapist',
      anyTherapist: 'Any available therapist',
      type: 'Session Type',
      typeHint: 'e.g. Speech Therapy',
      anyType: 'Any type',
      room: 'Room',
      anyRoom: 'No room',
      start: 'Date & Time',
      submit: 'Schedule Session',
    },
    reschedule: {
      start: 'New Date & Time',
      submit: 'Reschedule',
      conflict: 'That time clashes with another session for this therapist.',
    },
    invoice: {
      client: 'Patient',
      selectClient: SELECT_A_CLIENT,
      service: 'Service',
      serviceCustom: 'Custom amount',
      amount: 'Amount (₹)',
      amountHint: 'e.g. 2500',
      dueDate: 'Due Date',
      notes: 'Notes',
      notesHint: 'e.g. June speech-therapy sessions',
      submit: 'Create Invoice',
    },
    payment: {
      balance: 'Outstanding balance: {{amount}}',
      amount: 'Amount (₹)',
      method: 'Payment Mode',
      selectMethod: 'Select a mode',
      reference: 'Reference (cheque no / UTR / txn id)',
      date: 'Payment date',
      submit: 'Record Payment',
    },
    homeTask: {
      title: 'Task',
      titleHint: 'e.g. Practice eye contact during playtime (10 min)',
      category: 'Category',
      selectCategory: 'Select a category',
      instructions: 'Instructions',
      instructionsHint: 'Optional guidance for the parent',
      submit: 'Assign Task',
    },
  },
  login: {
    welcome: 'Welcome back',
    subtitle: 'Sign in to your TherapyOS account',
    selectRole: 'Select Your Role',
    email: 'Email address',
    emailPlaceholder: 'you@clinic.com',
    password: 'Password',
    passwordPlaceholder: 'Enter your password',
    signIn: 'Sign in',
    signInTo: 'Sign In to {{portal}}',
    selectClinic: 'Select your clinic',
    invalidCredentials: 'Email or password is incorrect',
    accountLocked: 'Account temporarily locked. Try again later.',
    forgotPassword: 'Forgot password?',
    rememberMe: 'Remember me',
    noAccount: "Don't have an account?",
    contactAdmin: 'Contact Admin',
    brandLine1: 'Empowering therapy.',
    brandLine2: 'One child at a time.',
    brandBlurb:
      'Comprehensive clinic management for paediatric therapy centres — scheduling, billing, clinical documentation, and parent engagement in one platform.',
    feature: {
      itpBuilder: 'ITP Builder',
      soapNotes: SOAP_NOTES,
      smartBilling: 'Smart Billing',
      parentPortal: 'Parent Portal',
      progressTracking: 'Progress Tracking',
    },
    roles: {
      CLINIC_ADMIN: { title: 'Admin Portal', blurb: 'Full system control and management' },
      THERAPIST: { title: 'Therapist Portal', blurb: 'Manage patients and therapy sessions' },
      PARENT: { title: 'Patient Portal', blurb: "Track your child's progress" },
      FINANCE: { title: 'Finance Portal', blurb: 'Billing, invoicing, and payroll' },
    },
    tagline: 'Secure • DPDP Compliant • Enterprise Grade',
  },
  forgot: {
    title: 'Reset your password',
    subtitle: "Enter your email and we'll send you a reset link.",
    submit: 'Send reset link',
    sentTitle: 'Check your email',
    sentBody:
      "If an account exists for that email, we've sent a link to reset your password. The link expires in 1 hour.",
    backToLogin: 'Back to login',
  },
  pagination: {
    prev: 'Previous',
    next: 'Next',
    pageOf: 'Page {{page}} of {{total}}',
  },
  account: {
    changePassword: {
      title: 'Change password',
      subtitle: 'Update the password for your account.',
      current: 'Current password',
      new: 'New password',
      submit: 'Update password',
      success: 'Your password has been updated.',
      error: 'Could not change your password. Check your current password and try again.',
    },
  },
  setPassword: {
    title: 'Set your password',
    subtitle: 'Choose a password to activate your account.',
    newPassword: 'New password',
    confirmPassword: 'Confirm password',
    submit: 'Set password',
    missingToken: 'This link is invalid or missing its token.',
    error: 'This link is invalid or has expired. Ask your clinic to resend it.',
    doneTitle: 'Password set',
    doneBody: 'Your account is active. You can now sign in.',
    goToLogin: 'Go to login',
  },
  staffLogin: {
    title: 'Staff Console',
    subtitle: 'iTech internal access only',
    totp: 'Authenticator code',
    totpPlaceholder: '6-digit code',
    signIn: 'Sign In',
  },
  intake: {
    saveDraft: 'Save draft',
    submit: 'Submit to clinic',
    next: 'Next',
    back: 'Back',
    stepOf: 'Step {{current}} of {{total}}',
    saveChanges: 'Save changes',
    changesSaved: 'Changes saved',
    addGuardian: 'Add guardian',
    removeGuardian: 'Remove',
    guardiansHint: 'Add all parents or guardians. At least one is required.',
    selectPlaceholder: 'Select…',
    submittedBanner: "Submitted — the clinic team is reviewing your child's details.",
    report: {
      title: 'Intake Report (PDF)',
      subtitle: 'The signed assessment report generated by the clinic.',
      download: 'Download Report',
      notReady: 'The clinic has not generated the report yet.',
    },
    status: {
      form_pending: 'Form Pending',
      form_filled: 'Form Filled',
      under_review: 'Under Review',
      scheduled: 'Scheduled',
      arrived: 'Arrived',
      completed: 'Completed',
    },
    schedule: {
      title: 'Schedule appointment',
      therapist: 'Therapist',
      dateTime: 'Date & time',
      selectTherapist: 'Select a therapist…',
      book: 'Schedule',
      scheduledWith: 'Appointment scheduled with {{name}}',
      booked: 'Appointment scheduled',
      inCalendar: 'The appointment is in the Sessions calendar.',
      markArrived: 'Mark as arrived',
      arrivedNote: 'Patient marked as arrived ✓',
    },
    sign: {
      capture: 'Capture signature',
      replace: 'Replace signature',
      current: 'Current signature',
      canvasLabel: 'Signature drawing area',
      clear: 'Clear',
      save: 'Save signature',
    },
    documents: {
      title: 'Document',
      subtitle: 'Sign off and generate the patient intake PDF.',
      generate: 'Generate PDF',
      generating: 'Generating…',
      download: 'Download PDF',
    },
    section: {
      basicDetails: 'Basic Patient Details',
      birthHistory: 'Birth & Neonatal History',
      postNatal: 'Post-Natal History',
      milestones: 'Developmental Milestones',
      motorSkills: 'Motor Skills, Health & Safety',
      dailyLiving: 'Daily Living & Feeding',
      speechLanguage: 'Speech & Language Profile',
      preferencesGoals: 'Preferences & Therapy Goals',
    },
    sectionHint: {
      basicDetails: "Your child's basic information.",
      birthHistory: "About your child's birth and early days.",
      postNatal: "Your child's health after birth.",
      milestones: 'Key developmental stages your child reached.',
      motorSkills: 'How your child moves, plus health & safety.',
      dailyLiving: 'Daily routines and eating habits.',
      speechLanguage: 'How your child communicates.',
      preferencesGoals: "Personalise your child's therapy experience.",
    },
    field: {
      firstName: 'First name',
      lastName: 'Last name',
      dateOfBirth: 'Date of birth',
      gender: 'Gender',
      primaryLanguage: 'Primary language spoken at home',
      addressLine1: 'Address line 1',
      addressLine2: 'Address line 2 (optional)',
      city: 'City',
      state: 'State',
      pinCode: 'PIN code',
      primaryContact: 'Primary contact number',
      secondaryContact: 'Secondary contact number (optional)',
      email: 'Email address',
      guardians: 'Parent / guardian details',
      guardianName: 'Name',
      guardianRelationship: 'Relationship',
      guardianContact: 'Contact',
      guardianEmail: 'Email',
      pregnancyDuration: 'Duration of pregnancy',
      deliveryType: 'Type of delivery',
      birthWeightKg: 'Weight at birth (kg)',
      criedAfterBirth: 'When did the child cry after birth?',
      jaundiceHistory: 'History of jaundice after birth?',
      jaundiceDetails: 'Describe the jaundice history',
      appearanceChanges: 'Any changes in appearance at birth?',
      appearanceDetails: 'Describe the appearance changes',
      breastfeeding: 'Was your child breastfed?',
      seizureHistory: 'Any history of seizures?',
      seizureDetails: 'Describe the seizure history',
      complicationsAfterBirth: 'Any complications after birth?',
      complicationDetails: 'Describe the complications',
      neckControlAge: 'Age when head held up steadily (months)',
      didRoll: 'Did your child learn to roll over?',
      didCrawl: 'Did your child crawl before walking?',
      ageSat: 'Age when your child sat (months)',
      ageStood: 'Age when your child stood (months)',
      ageStartedRunning: 'Age when your child started running (months)',
      gross: 'Gross motor skills',
      fine: 'Fine motor skills',
      allergies: 'Any known allergies?',
      safetyStrangers: 'Approaches strangers without hesitation?',
      safetyTraffic: 'Understands to stop / look for vehicles?',
      safetyHazards: 'Aware of dangers (heights, hot surfaces, sharp objects)?',
      safetyWandering: 'Wanders or runs off in public places?',
      safetyMouthing: 'Puts unsafe items in their mouth?',
      specialDiet: 'Does your child follow a specific diet?',
      specialDietOther: 'Please specify the diet',
      feedsIndependently: 'Can your child feed independently?',
      screenTimeDuringMeals: 'Needs screen time during meals?',
      mealCompletionTime: 'Time taken to complete a meal',
      foodTextures: 'Food textures your child can eat',
      drinksWaterAfterEachServing: 'Drinks water after each serving?',
      sleepingPatterns: 'Sleeping patterns or any sleep issues',
      toiletingSkills: 'How does your child manage toileting?',
      brushingSkills: 'Can your child brush their teeth?',
      bathingSkills: 'How does your child manage bathing?',
      dressingSkills: 'Help needed with getting dressed',
      communicationMethods: 'How does your child communicate?',
      meaningfulWordCount: 'Approximate number of meaningful words',
      doesBabble: 'Does your child babble?',
      canAskForThings: 'Can ask for things using words?',
      asksForItems: 'What items does your child ask for?',
      canLabelObjects: 'Can name objects from books / flashcards',
      imitatesSingleWords: 'Can imitate single words?',
      imitatesPhrases: 'Can imitate short phrases?',
      repeatsMemorizedSpeech: 'Repeats memorized speech (movies, songs)?',
      memorizedSpeechDetails: 'Describe the memorized speech',
      fillsBlanksInSongs: 'Fills in blanks in familiar songs?',
      songsList: 'Which songs does your child know?',
      favoritesGamesRhymesFood: 'Favourite games, toys, rhymes and foods',
      expectedChangesPostTherapy: 'What changes do you hope to see after therapy?',
    },
    opt: {
      gender: { male: 'Male', female: 'Female', other: 'Other' },
      pregnancy: { 'full-term': 'Full term', 'pre-term': 'Pre term' },
      delivery: {
        normal: 'Normal',
        'c-section': 'C-section',
        breech: 'Breech presentation',
        forceps: 'Forceps',
      },
      cry: { immediately: 'Immediately', delayed: 'Delayed' },
      tristate: { yes: 'Yes', no: 'No', sometimes: 'Sometimes' },
      safety: { yes: 'Yes', no: 'No', sometimes: 'Sometimes', 'not-sure': 'Not sure' },
      diet: {
        regular: 'Regular (no restrictions)',
        vegetarian: 'Vegetarian',
        'gluten-free': 'Gluten-free',
        'dairy-free': 'Dairy-free',
        other: 'Other',
      },
      mealTime: {
        '20-30': '20–30 mins',
        '30-40': '30–40 mins',
        '40-50': '40–50 mins',
        '50-60': '50–60 mins',
        '60-plus': '60+ mins',
      },
      texture: {
        'smooth-puree': 'Smooth puree / mashed',
        'soft-solids': 'Soft solids',
        'hard-solids': 'Hard solids',
        'tough-chewy': 'Tough / chewy',
      },
      toileting: {
        independent: 'Goes on their own',
        reminders: 'Needs reminders',
        'physical-help': 'Needs physical help',
        'not-trained': 'Not yet toilet trained',
      },
      selfcare: {
        independent: 'Does it alone',
        supervision: 'With supervision / reminders',
        'partial-help': 'Needs help but tries',
        'fully-dependent': 'Fully dependent',
      },
      dressing: {
        independent: 'Does it alone',
        'some-help': 'Struggles with buttons / zips',
        'most-help': 'Needs help with most clothing',
        'fully-dependent': 'Fully dependent on caregiver',
      },
      gross: {
        jumping: 'Jumping',
        hopping: 'Hopping',
        'throwing-catching': 'Throwing & catching a ball',
        'climbing-stairs': 'Climbing stairs',
        'standing-one-leg': 'Standing on one leg',
        'alternate-step-climbing': 'Alternate-step climbing',
      },
      fine: {
        buttoning: 'Buttoning',
        lacing: 'Lacing',
        'tripod-grasp': 'Tripod grasp',
        writing: 'Writing',
        'scissor-cutting': 'Scissor cutting',
        'colouring-in-boundaries': 'Colouring inside boundaries',
      },
      comm: {
        crying: 'Crying',
        'taking-to-object': 'Taking caregiver to object',
        'placing-hand': "Placing parent's hand on object",
        grabbing: 'Grabbing object',
        pointing: 'Pointing',
        naming: 'Naming / pointing at object',
        'two-word': '2-word combinations',
        'three-four-word': '3–4 word combinations',
        'sign-language': 'Sign language',
      },
    },
  },
  consultantInvoice: {
    title: 'Invoices',
    subtitle: 'Generate and submit your monthly invoices',
    adminTitle: 'Consultant Invoices',
    adminSubtitle: 'Review, approve and pay consultant invoices',
    month: 'Month',
    generate: 'Generate invoice',
    generateError: 'Could not generate — an invoice for this month may exist, or no rate is set.',
    empty: 'No invoices yet. Generate one for a month.',
    emptyAdmin: 'No consultant invoices to review.',
    submit: 'Submit for approval',
    approve: 'Approve',
    reject: 'Reject',
    rejectReason: 'Reason for rejection',
    markPaid: 'Mark paid',
    sessionsCount: '{{count}} sessions',
    gstNote: 'Subtotal {{subtotal}} + GST {{gst}}',
    rejectedNote: 'Rejected: {{note}}',
    status: {
      draft: 'Draft',
      submitted: 'Submitted',
      approved: 'Approved',
      rejected: 'Rejected',
      paid: 'Paid',
    },
  },
  proforma: {
    title: 'Proformas',
    empty: 'No proformas to review.',
    adminTitle: 'Proformas',
    adminSubtitle: 'Generate the monthly cycle, then send drafts to patients',
    emptyAdmin: 'No proformas yet. Generate a cycle for a month.',
    generateCycle: 'Generate Cycle',
    generateTitle: 'Generate proforma cycle',
    generateHint:
      'Creates a draft per active patient with sessions scheduled that month, priced at the per-session fee. You can adjust each before sending.',
    generate: 'Generate',
    month: 'Billing month',
    sessionFee: 'Fee per session (₹)',
    sessions: 'Sessions',
    discount: 'Discount (₹)',
    discountNote: 'Less discount {{amount}}',
    adjust: 'Adjust',
    adjustTitle: 'Adjust proforma',
    send: 'Send to patient',
    approve: 'Approve',
    decline: 'Decline',
    declineReason: 'Reason for declining',
    declinedNote: 'Declined: {{reason}}',
    convert: 'Convert to invoice',
    convertTitle: 'Convert to invoice',
    convertApproved: 'Convert approved ({{count}})',
    convertedNote: 'Invoice {{number}}',
    invoiceDate: 'Invoice date',
    netTerm: 'Payment term',
    netDays: 'Net {{count}} days',
    sessionsCount: '{{count}} sessions',
    gstNote: 'Subtotal {{subtotal}} + GST {{gst}}',
    status: {
      draft: 'Draft',
      sent: 'Sent',
      approved: 'Approved',
      declined: 'Declined',
      converted: 'Converted',
    },
  },
  timesheet: {
    title: 'Timesheet',
    subtitle: 'Your sessions and earnings by period',
    period: {
      this_week: 'This week',
      this_month: 'This month',
      this_quarter: 'This quarter',
      half_year: LAST_6_MONTHS,
      this_year: 'This year',
      all: 'All time',
    },
    summary: { sessions: 'Sessions', hours: 'Hours', earnings: 'Earnings' },
    col: {
      date: 'Date',
      client: 'Patient',
      type: 'Type',
      duration: 'Duration',
      status: 'Status',
      pay: 'Pay %',
      amount: 'Amount',
    },
    empty: 'No sessions in this period.',
  },
  documents: {
    title: 'Documents',
    myTitle: 'My Documents',
    mySubtitle: 'Contracts and documents shared by your clinic',
    type: {
      contract: 'Contract',
      engagement_letter: 'Engagement Letter',
      cpd_certificate: 'CPD Certificate',
      id_proof: 'ID Proof',
      policy: 'Policy',
      other: 'Other',
    },
    version: 'v{{n}}',
    superseded: 'Superseded',
    download: 'Download',
    delete: 'Delete document',
    empty: 'No documents yet.',
    typeLabel: 'Document type',
    titleLabel: 'Title',
    fileLabel: 'File (PDF or image)',
    uploadCta: 'Upload',
    noFile: 'Choose a file to upload.',
  },
  rbac: {
    createRole: 'Create Role',
    saveAccess: 'Save Access',
    systemOverrideNote: 'Built-in role — your changes apply to this clinic only.',
    roleName: 'Role name',
    roleNamePlaceholder: 'e.g. Receptionist',
    userCount_one: '{{count}} user',
    userCount_other: '{{count}} users',
    system: 'System',
    view: 'View',
    noModules: 'No module access yet',
    empty: 'No roles defined.',
    editorSubtitle: 'Set the access level per module.',
    level: {
      '0': 'None',
      '1': 'View',
      '2': 'Edit',
      '3': 'Full',
    },
    module: {
      dashboard: 'Dashboard',
      inbox: 'Inbox',
      patients: 'Patients',
      therapists: 'Therapists',
      schedule: 'Schedule',
      sessions: 'Sessions',
      waitlist: 'Waitlist',
      proformas: 'Proformas',
      invoices: 'Invoices',
      payroll: 'Payroll',
      reports: 'Reports',
      analytics: 'Analytics',
      followUps: 'Follow-ups',
      soapNotes: SOAP_NOTES,
      timesheet: 'Timesheet',
      documents: 'Documents',
      users: 'Users',
      settings: 'Settings',
    },
  },
  nav: {
    dashboard: 'Dashboard',
    inbox: 'Inbox',
    clients: 'Patients',
    therapists: 'Therapists',
    sessions: 'Sessions',
    reports: 'Reports',
    finance: 'Finance',
    analytics: 'Analytics',
    settings: 'Settings',
    staffUsers: 'Staff Users',
    users: 'Users',
    myClients: MY_CLIENTS,
    schedule: 'Schedule',
    availability: 'Availability',
    sessionNotes: SOAP_NOTES,
    therapyPlans: THERAPY_PLANS,
    timesheet: 'Timesheet',
    proformas: 'Proformas',
    consultantInvoices: 'Consultant Invoices',
    documents: 'Documents',
    messages: 'Messages',
    progress: 'Progress',
    homeTasks: HOME_TASKS,
    patientDetails: 'Patient Details',
    billing: 'Billing',
    invoices: 'Invoices',
    catalogue: 'Catalogue',
    payments: 'Payments',
    payroll: 'Payroll',
    followUps: 'Follow-ups',
    waitlist: 'Waitlist',
    sessionRequests: 'Session Requests',
    book: 'Book Appointment',
    therapyPlan: 'Therapy Plan',
    appointments: 'Appointments',
    homeProgramme: 'Home Programme',
    clinics: 'Clinics',
  },
  book: {
    title: 'Book Appointment',
    subtitle: 'Request a therapy session — the clinic will confirm a slot',
    child: 'Patient',
    selectChild: 'Select patient',
    preferredDate: 'Preferred Date',
    preferredTime: 'Preferred Time',
    timePlaceholder: 'e.g. Mornings',
    therapyType: 'Therapy Type',
    notes: 'Notes',
    submit: 'Submit Request',
    requestTitle: 'Request Session',
    currentSchedule: 'Current Schedule',
    noSchedule: 'No sessions scheduled yet',
    session: 'Session',
    phone: 'Phone',
    phoneNote: 'Contact phone: {{phone}}',
    save: 'Save',
    myRequests: 'My Requests',
    empty: 'No requests yet',
    noPreference: 'No date preference',
    statuses: { pending: 'Pending', approved: 'Approved', declined: 'Declined' },
  },
  sessionRequests: {
    title: 'Session Requests',
    subtitle: 'Approve or decline parent session requests',
    empty: 'No session requests',
    approve: 'Approve',
    decline: 'Decline',
    approveTitle: 'Approve & Schedule',
    approveConfirm: 'Approve & Schedule',
    scheduledStart: 'Session Date & Time',
    therapist: 'Therapist',
    anyTherapist: 'Any available therapist',
    room: 'Room',
    noRoom: 'No room',
    col: {
      patient: 'Patient',
      requestedBy: 'Requested By',
      preferred: 'Preferred',
      type: 'Type',
      status: 'Status',
    },
  },
  waitlist: {
    title: 'Waitlist',
    subtitle: 'Children awaiting a therapy slot',
    addTitle: 'Add to Waitlist',
    add: 'Add',
    childName: 'Child Name',
    age: 'Age',
    concern: 'Concern',
    preferredSlots: 'Preferred Slots',
    priority: 'Priority',
    contact: 'Mark Contacted',
    schedule: 'Mark Scheduled',
    remove: 'Remove',
    empty: 'The waitlist is empty',
    col: {
      child: 'Child',
      age: 'Age',
      concern: 'Concern',
      slots: 'Preferred Slots',
      priority: 'Priority',
      waitSince: 'Wait Since',
      status: 'Status',
    },
    priorities: { low: 'Low', medium: 'Medium', high: 'High' },
    statuses: {
      waiting: 'Waiting',
      contacted: 'Contacted',
      scheduled: 'Scheduled',
      removed: 'Removed',
    },
  },
  followUps: {
    title: 'Follow-up Escalation',
    subtitle: 'Track and resolve overdue follow-ups across the clinic',
    newTitle: 'New Follow-up',
    create: 'Create',
    resolve: 'Resolve',
    overdue: 'Overdue',
    tabAll: 'All',
    type: 'Type',
    priority: 'Priority',
    titleLabel: 'Title',
    sla: 'SLA Due',
    empty: 'No follow-ups',
    col: {
      type: 'Type',
      patient: 'Patient',
      priority: 'Priority',
      sla: 'SLA',
      created: 'Created',
      status: 'Status',
      assigned: 'Assigned',
    },
    types: {
      proforma_declined: 'Proforma Declined',
      payment_overdue: 'Payment Overdue',
      intake_pending: 'Intake Pending',
      session_missed: 'Session Missed',
      document_pending: 'Document Pending',
      review_due: 'Review Due',
      custom: 'Custom',
    },
    priorities: { low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' },
    statuses: { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved' },
    resolveTitle: 'Resolve Follow-up',
    resolutionNote: 'Resolution note',
    slaStats: {
      resolved: 'Resolved',
      onTimePct: 'On-time',
      breached: 'SLA breached',
      avgHours: 'Avg. resolution',
      openOverdue: 'Open overdue',
    },
    alerts: {
      title: 'Follow-up Alerts',
      open: 'Open',
      overdue: 'Overdue',
      urgent: 'Urgent',
      viewAll: 'View all',
      none: 'No open follow-ups',
    },
  },
  inbox: {
    title: 'Inbox',
    subtitle: 'Your notifications and alerts',
    all: 'All',
    unread: 'Unread',
    markAllRead: 'Mark all read',
    empty: 'You’re all caught up',
    viewAll: 'View all notifications',
  },
  finance: {
    dashboard: {
      invoiced: 'Total Invoiced',
      collected: 'Collected',
      collectedPct: '{{pct}}% collected',
      overdue: 'Overdue',
      overdueCount_one: '{{count}} invoice overdue',
      overdueCount_other: '{{count}} invoices overdue',
      proformasPending: 'Proformas Pending',
      proformasPendingSub: 'Sent + Draft',
      generateCycle: 'Generate Cycle',
      outstanding: 'Outstanding',
      invoices: 'Invoices',
      revenueTrend: 'Revenue',
      revenueTrendSub: 'Collected, last 6 months',
      title: 'Finance Dashboard',
      subtitle: 'Financial overview',
      newInvoice: 'New Invoice',
      totalRevenue: 'Total Revenue',
      processedToday: 'Processed Today',
      netProfit: 'Net Profit',
      thisMonth: 'this month',
      revenueExpenses: 'Revenue vs Expenses',
      revenueExpensesSub: LAST_6_MONTHS,
      revenueSeries: 'Revenue',
      expensesSeries: 'Expenses',
      paymentMethods: 'Payment Methods',
      paymentMethodsSub: 'This month',
      outstandingInvoices: 'Outstanding Invoices',
      pendingPayments: '{{count}} pending payments',
      noOutstanding: 'No outstanding invoices.',
      recentTransactions: 'Recent Transactions',
      latestPayments: 'Latest payments',
      noPayments: 'No payments recorded yet.',
      payrollSummary: 'Payroll Summary',
      colTherapist: 'Therapist',
      colRole: 'Role',
      colSessions: 'Sessions',
      colAmount: 'Amount',
      colStatus: 'Status',
      therapistRole: 'Therapist',
      noPayroll: 'No payroll runs yet.',
      due: 'Due {{date}}',
      viewAll: 'View All',
    },
    analytics: {
      title: 'Analytics',
      subtitle: 'Financial Overview',
      heading: 'Financial Analytics',
      headingSub: 'Comprehensive financial performance metrics',
      revExpProfit: 'Revenue vs Expenses vs Profit',
      sixMonth: '6-month overview',
      revenue: 'Revenue',
      expenses: 'Expenses',
      profit: 'Profit',
      paymentMethods: 'Payment Methods',
      revenueBySource: 'Revenue by source',
      monthlyProfit: 'Monthly Profit Trend',
      netProfitOverTime: 'Net profit over time',
      noData: NO_DATA_YET,
    },
    inbox: {
      title: 'Inbox',
      subtitle: 'Proformas awaiting finance action',
      tab_all: 'All',
      tab_approved: 'Approved',
      tab_declined: 'Declined',
      approvedTitle: 'Proforma Approved — {{name}}',
      declinedTitle: 'Proforma Declined — {{name}}',
      reason: 'Reason: {{reason}}',
      followUpRaised: 'Follow-up raised',
      empty: 'Nothing needs action.',
    },
    proformas: {
      empty: 'No proformas yet.',
      tab_all: 'All',
      tab_draft: 'Draft',
      tab_sent: 'Sent',
      tab_approved: 'Approved',
      col: {
        id: 'ID',
        patient: 'Patient',
        sessions: 'Sessions',
        amount: 'Amount',
        month: 'Month',
        status: 'Status',
      },
    },
    invoices: {
      title: 'Invoices',
      count: '{{count}} invoices',
      add: 'New Invoice',
      addSubtitle: 'Raise a new invoice for a client',
      empty: 'No invoices yet.',
      searchPlaceholder: 'Search by invoice number or notes…',
      statusFilter: FILTER_BY_STATUS,
      allStatus: ALL_STATUS,
      recordPayment: 'Record Payment',
      recordPaymentSubtitle: 'Record a payment against this invoice',
      markPaid: 'Mark Paid',
      locked: 'Locked (paid) — corrections via credit note',
      creditNote: 'Credit note',
      receipt: 'Receipt',
      col: {
        number: 'Invoice',
        client: 'Patient',
        amount: 'Amount',
        issued: 'Issued',
        balance: 'Balance',
        due: 'Due',
        payment: 'Payment',
        status: 'Status',
      },
    },
    creditNote: {
      title: 'Credit note',
      subtitle: 'Issue a credit note against this paid invoice',
      none: 'No credit notes yet.',
      amount: 'Credit amount (₹)',
      reason: 'Reason',
      issue: 'Issue credit note',
    },
    reports: {
      title: 'Reports',
      count: '{{count}} reports',
    },
    catalogue: {
      title: 'Service Catalogue',
      count: '{{count}} services',
      add: 'Add service',
      addTitle: 'Add service',
      editTitle: 'Edit service',
      subtitle: 'A named service with a default price; invoices price from it.',
      name: 'Service name',
      price: 'Default price (₹)',
      priceHint: 'e.g. 2500',
      description: 'Description (optional)',
      save: 'Save service',
      empty: 'No services yet — add your first fee-schedule item.',
      edit: 'Edit service',
      delete: 'Remove service',
      inactive: 'Inactive',
    },
  },
  reports: {
    recent: 'Recent reports',
    generate: 'Generate report',
    generateError: 'Could not start that report. Please try again.',
    loading: 'Loading reports…',
    error: 'Could not load reports.',
    empty: 'No reports yet — generate one above.',
    download: 'Download',
    field: {
      type: 'Report type',
      client: 'Patient',
      selectClient: SELECT_A_CLIENT,
      from: 'From',
      to: 'To',
    },
    type: {
      client_progress: 'Patient Progress',
      attendance: 'Attendance',
      revenue: 'Revenue',
      itp: 'Therapy Plan',
      monthly_summary: 'Monthly Summary',
      quarterly_review: 'Quarterly Review',
      annual_report: 'Annual Report',
      therapist_performance: 'Therapist Performance',
      clinical_outcomes: 'Clinical Outcomes',
      profit_loss: 'Profit & Loss Statement',
      tax_report: 'Tax Report',
      revenue_forecast: 'Revenue Forecast',
      payroll_summary: 'Payroll Summary',
      intake_assessment: 'Intake Assessment',
    },
    status: {
      queued: 'Queued',
      running: 'Generating…',
      succeeded: 'Ready',
      failed: 'Failed',
    },
    col: {
      report: 'Report',
      status: 'Status',
      created: 'Created',
      action: '',
    },
  },
  invoiceStatus: {
    draft: 'Draft',
    sent: 'Sent',
    partial: 'Partial',
    paid: 'Paid',
    overdue: 'Overdue',
    void: 'Void',
  },
  payroll: {
    title: 'Payroll',
    count: '{{count}} runs',
    add: 'New Payroll Run',
    addSubtitle: 'Create a payroll run for a pay period',
    empty: 'No payroll runs yet.',
    rosterEmpty: 'No therapists to show.',
    tab_all: 'All',
    tab_on_roll: 'On-Roll',
    tab_consultant: 'Consultant',
    category_on_roll: 'On-Roll',
    category_consultant: 'Consultant',
    perMonth: '{{amount}}/mo',
    perSession: '{{amount}}/session',
    sessionsCount: SESSIONS_UNIT,
    status_paid: 'Paid',
    status_pending: 'Pending',
    colPeriod: 'Period',
    colTotal: 'Total',
    colStatus: 'Status',
    pendingPayroll: 'Pending Payroll',
    paidThisPeriod: 'Paid This Period',
    totalTherapists: 'Total Therapists',
    runs: 'Payroll Runs',
    form: {
      period: 'Pay Period',
      periodHint: 'e.g. June 2026',
      start: 'Period Start',
      end: 'Period End',
      notes: 'Notes',
      submit: 'Create Run',
      therapist: 'Therapist',
      selectTherapist: 'Select a therapist',
      gross: 'Gross Pay (₹)',
      sessions: 'Sessions',
      addSubmit: 'Add Payslip',
    },
    detail: {
      title: 'Payroll Run',
      total: 'Total: {{amount}}',
      addPayslip: 'Add Payslip',
      addSubtitle: 'Add a therapist payslip to this run',
      process: 'Process',
      empty: 'No payslips yet.',
      colTherapist: 'Therapist',
      colRole: 'Role',
      colSessions: 'Sessions',
      colRate: 'Rate/Session',
      colGross: 'Gross',
      role: 'Therapist',
    },
  },
  payrollStatus: {
    draft: 'Draft',
    processed: 'Processed',
    paid: 'Paid',
  },
  paymentMethod: {
    cash: 'Cash',
    card: 'Card',
    upi: 'UPI',
    bank_transfer: 'Bank Transfer',
    razorpay: 'Razorpay',
    insurance: 'Insurance',
    cheque: 'Cheque',
  },
  home: {
    assign: 'Assign Task',
    assignSubtitle: 'Add a home-programme task for this client',
  },
  homeCategory: {
    social: 'Social',
    sensory: 'Sensory',
    cognitive: 'Cognitive',
    communication: 'Communication',
    motor: 'Motor',
    other: 'Other',
  },
  analytics: {
    title: 'Analytics',
    subtitle: 'Clinic performance at a glance',
    range: 'Date range',
    lastMonths: 'Last {{count}} months',
    export: 'Export CSV',
    financeSubtitle: 'Revenue and collections at a glance',
    totalClients: 'Total Patients',
    completedSessions: 'Completed Sessions',
    sessionsByStatus: 'Sessions by Status',
    clientsByStatus: 'Patients by Status',
    invoicesByStatus: 'Invoices by Status',
    collections: 'Invoiced vs Collected (₹)',
    revenueTrend: 'Revenue Trend',
    revenueTrendSub: 'Revenue and sessions over the last 6 months',
    diagnosisDistribution: 'Diagnosis Distribution',
    diagnosisSub: 'Active patients by primary diagnosis',
    therapistUtilization: 'Therapist Utilization',
    utilizationSub: 'Completed sessions this month, relative to the busiest therapist',
    noData: NO_DATA_YET,
  },
  analyticsReport: {
    title: 'Report & Export',
    range: 'Date range',
    from: 'From',
    to: 'To',
    staffType: 'Staff type',
    exportPdf: 'PDF',
    exportXlsx: 'Excel',
    empty: 'No activity in the selected range',
    statusTitle: 'Session status breakdown',
    revenueByTypeTitle: 'Revenue by therapist type',
    unattributed: 'Unattributed',
    presets: { week: 'Last 7 days', month: 'Last 30 days', custom: 'Custom range' },
    staff: { all: 'All staff', on_roll: 'On-roll', consultant: 'Consultant' },
    kpi: {
      revenue: 'Revenue',
      sessions: 'Sessions',
      completed: 'Completed',
      satisfaction: 'Avg. satisfaction',
    },
    col: {
      therapist: 'Therapist',
      type: 'Type',
      sessions: 'Sessions',
      completed: 'Completed',
      revenue: 'Revenue',
    },
  },
  settings: {
    title: 'Settings',
    subtitle: 'Manage your clinic profile',
    cards: {
      profile: { title: 'Clinic Profile', subtitle: 'Name, address, GST, logo' },
      users: { title: 'Users & Roles', subtitle: 'Manage user accounts and permissions' },
      therapyTypes: { title: 'Therapy Types', subtitle: 'Configure therapy categories and rates' },
      sessionRules: {
        title: 'Session Rules',
        subtitle: 'Status rules, cancellation policy, SLA timers',
      },
      billing: { title: 'Billing Config', subtitle: 'Proforma cycle, invoice numbering, tax' },
      notifications: {
        title: 'Notifications',
        subtitle: 'WhatsApp, email, in-app alert preferences',
      },
      rooms: { title: 'Rooms', subtitle: 'Bookable therapy rooms used in scheduling' },
      security: { title: 'Security', subtitle: 'Change your password' },
    },
    sessionRules: {
      statusTitle: 'Session Status Rules',
      statusSub: 'How each status affects billing and therapist pay',
      cancelTitle: 'Cancellation & Buffer',
      cancelWindow: 'Cancellation window',
      buffer: 'Session buffer time',
      hours: '{{count}} hours before session',
      minutes: '{{count}} minutes between sessions',
      status: {
        completed: { label: 'Completed', billing: 'Full session rate', pay: 'Therapist: Full' },
        noShow: { label: 'No Show', billing: '₹200 cancellation fee', pay: 'Therapist: None' },
        clientLate: {
          label: 'Client Late',
          billing: 'Full session rate (if started)',
          pay: 'Therapist: Full',
        },
        cancelledEarly: {
          label: 'Cancelled (24h+ notice)',
          billing: 'No charge',
          pay: 'Therapist: None',
        },
        cancelledLate: {
          label: 'Cancelled (within 24h)',
          billing: '50% session rate',
          pay: 'Therapist: 30%',
        },
        consultantAttended: {
          label: 'Consultant Attended',
          billing: 'Full session rate',
          pay: 'Therapist: 30% of rate',
        },
      },
    },
    name: 'Clinic Name',
    legalName: 'Legal Name',
    email: 'Contact Email',
    phone: 'Contact Phone',
    timezone: 'Timezone',
    gstin: 'GSTIN',
    logoUrl: 'Logo URL',
    address: 'Address',
    line1: 'Address Line 1',
    line2: 'Address Line 2',
    city: 'City',
    state: 'State',
    postalCode: 'Postal Code',
    country: 'Country (ISO)',
    save: 'Save Changes',
    saved: 'Settings saved.',
    tabs: {
      profile: 'Profile',
      therapyTypes: 'Therapy Types',
      rooms: 'Rooms',
      notifications: 'Notifications',
      billing: 'Billing & Rules',
      security: 'Security',
    },
    rooms: {
      title: 'Rooms',
      subtitle: 'Manage bookable therapy rooms used in scheduling',
      name: 'Room Name',
      add: 'Add Room',
      active: 'Active',
      inactive: 'Inactive',
      activate: 'Activate',
      deactivate: 'Deactivate',
      empty: 'No rooms yet',
    },
    therapyTypes: {
      title: 'Therapy Types',
      subtitle: 'Manage the session types used across scheduling and notes',
      name: 'Name',
      duration: 'Duration (min)',
      add: 'Add Type',
      mins: '{{count}} min',
      active: 'Active',
      inactive: 'Inactive',
      activate: 'Activate',
      deactivate: 'Deactivate',
      empty: 'No therapy types yet',
    },
    billing: {
      proformaCycle: 'Proforma cycle',
      proformaDay: 'Generate proformas on day',
      autoSend: 'Auto-send proforma to patients',
      autoConvert: 'Auto-convert approved proformas to invoices',
      invoiceTax: 'Invoice numbering & tax',
      invoicePrefix: 'Invoice prefix',
      invoiceNextSeq: 'Next invoice number',
      proformaPrefix: 'Proforma prefix',
      gstRate: 'GST rate',
      payRules: 'Session pay rules (% of rate)',
      payCompleted: 'Completed',
      payClientLate: 'Client came late',
      payConsultantAbsent: 'Consultant attended, client absent',
      payCancelled: 'Cancelled (advance)',
      payNoShow: 'No show',
      payments: 'Payments',
      acceptedModes: 'Accepted payment modes',
      noShowFee: 'No-show fee',
      mode: {
        cash: 'Cash',
        upi: 'UPI',
        card: 'Card',
        bank_transfer: 'Bank transfer',
        insurance: 'Insurance',
        cheque: 'Cheque',
      },
      sla: 'Follow-up SLA timers (hours)',
      slaProformaDeclined: 'Proforma declined',
      slaPatientQuery: 'Patient query',
      slaInvoiceOverdue: 'Invoice overdue',
      slaConsultantInvoice: 'Consultant invoice pending',
    },
    notifications: {
      sessionReminders: 'Session reminders',
      sessionRemindersDesc: 'Email reminders before upcoming sessions.',
      paymentAlerts: 'Payment alerts',
      paymentAlertsDesc: 'Notify when invoices are paid or fall due.',
      progressReports: 'Progress reports',
      progressReportsDesc: 'Notify when a progress report is ready.',
      systemUpdates: 'System updates',
      systemUpdatesDesc: 'Platform and maintenance announcements.',
    },
    security: {
      subtitle: 'Change your account password. You will stay signed in on this device.',
    },
  },
  messages: {
    title: 'Messages',
    new: 'New message',
    newSubtitle: 'Start a conversation with a contact',
    empty: 'No messages yet.',
    conversation: 'Conversation',
    composer: 'Type a message…',
    searchPlaceholder: 'Search conversations…',
    noMatches: 'No conversations match your search.',
    noContacts: 'No contacts available to message.',
    selectContact: 'Select a contact',
    startChat: 'Start chat',
    pickConversation: 'Select a conversation to start chatting',
  },
  sa: {
    dashboard: {
      title: 'Platform Overview',
      subtitle: 'All clinics on TherapyOS at a glance',
      empty: NO_DATA_YET,
      totalClinics: 'Total Clinics',
      activeClinics: 'Active Clinics',
      mrr: 'Monthly Recurring Revenue',
      health: 'Health (G / A / R)',
      healthHint: 'Green / Amber / Red',
      atRisk: 'At-Risk Clinics',
      atRiskHint: 'amber + red health',
      platformHealth: 'Platform Health',
      platformHealthSub: 'Clinics by RAG status',
      healthGreen: 'Healthy',
      healthAmber: 'At risk',
      healthRed: 'Critical',
      clinicsUnit: '{{count}} clinics',
      topBySessions: 'Top Clinics by Sessions',
      topByRevenue: 'Top Clinics by Revenue (₹)',
    },
    clinics: {
      title: 'Clinics',
      count: '{{count}} clinics',
      add: 'New Clinic',
      empty: 'No clinics onboarded yet.',
      searchPlaceholder: 'Search by name, plan, or status…',
      colName: 'Clinic',
      colStatus: 'Status',
      colPlan: 'Plan',
      colMrr: 'MRR',
      colTherapists: 'Therapists',
      colActiveClients: ACTIVE_CLIENTS,
    },
    subscriptions: {
      title: 'Subscriptions',
      count: '{{count}} clinics',
      empty: 'No subscriptions yet.',
      col: {
        clinic: 'Clinic',
        plan: 'Plan',
        status: 'Status',
        mrr: 'MRR',
        renews: 'Renews / Trial ends',
        actions: '',
      },
      markPaid: 'Mark paid',
      status: {
        active: 'Active',
        trialing: 'Trialing',
        past_due: 'Past due',
        suspended: 'Suspended',
        cancelled: 'Cancelled',
        none: 'No plan',
      },
    },
    usage: {
      title: 'Usage & Health',
      healthSummary: '{{green}} green · {{amber}} amber · {{red}} red',
      empty: 'No usage data yet.',
      col: {
        clinic: 'Clinic',
        health: 'Health',
        sessions: 'Sessions (30d)',
        whatsapp: 'WhatsApp',
        sms: 'SMS',
        pdfs: 'PDFs',
        storage: 'Storage',
        users: 'Active Users',
      },
      health: {
        green: 'Healthy',
        amber: 'At risk',
        red: 'Critical',
        unknown: 'Unknown',
      },
    },
    staff: {
      title: 'Staff Users',
      count: '{{count}} staff accounts',
      add: 'Add Staff User',
      empty: 'No staff accounts yet.',
      active: 'Active',
      inactive: 'Disabled',
      enable: 'Enable',
      disable: 'Disable',
      col: {
        member: 'Member',
        role: 'Role',
        status: 'Status',
        lastLogin: 'Last login',
        actions: '',
      },
      role: {
        PLATFORM_OWNER: 'Platform Owner',
        CUSTOMER_SUCCESS: 'Customer Success',
        SUPPORT: 'Support',
        READONLY_AUDITOR: 'Read-only Auditor',
      },
      form: {
        fullName: 'Full name',
        email: 'Email',
        role: 'Role',
        submit: 'Create account',
        error: 'Could not create the account (email may be in use).',
      },
      created: {
        title: 'Account created',
        intro: 'Share these credentials securely with {{email}}.',
        copy: 'Copy',
        warning: 'Shown once — the member should rotate this password on first login.',
        done: 'Done',
      },
    },
    clinicStatus: {
      active: 'Active',
      trial: 'Trial',
      trialing: 'Trial',
      suspended: 'Suspended',
      cancelled: 'Cancelled',
      past_due: 'Past Due',
    },
    detail: {
      back: 'Back to Clinics',
      notFound: 'Clinic not found.',
      overview: 'Overview',
      legalName: 'Legal Name',
      contactEmail: 'Contact Email',
      contactPhone: 'Contact Phone',
      timezone: 'Timezone',
      createdAt: 'Onboarded',
      subscription: 'Subscription',
      noSubscription: 'No active subscription.',
      plan: 'Plan',
      subStatus: 'Status',
      mrr: 'MRR',
      periodEnd: 'Renews',
      therapists: 'Therapists',
      activeClients: ACTIVE_CLIENTS,
      sessions: 'Sessions',
      invoiced: 'Invoiced',
      recentAudit: 'Recent Activity',
      noAudit: 'No recent activity.',
    },
    audit: {
      title: 'Audit Log',
      subtitle: 'Platform-wide activity across all clinics',
      empty: 'No audit entries match these filters.',
      allClinics: 'All clinics',
      filterClinic: 'Filter by clinic',
      filterAction: 'Filter by action…',
      filterFrom: 'From date',
      filterTo: 'To date',
      colTime: 'Time',
      colClinic: 'Clinic',
      colActor: 'Actor',
      colAction: 'Action',
      colEntity: 'Entity',
    },
    settings: {
      title: 'Platform Settings',
      subtitle: 'Feature flags and platform configuration',
      flags: 'Feature Flags',
      empty: 'No feature flags yet. Add one below.',
      enabled: 'Enabled',
      disabled: 'Disabled',
      enable: 'Enable',
      disable: 'Disable',
      addKey: 'Add a feature flag',
      addPlaceholder: 'e.g. parent_app_v2',
      add: 'Add',
      col: {
        key: 'Flag',
        state: 'State',
        updated: 'Updated',
      },
    },
    wizard: {
      title: 'Onboard a Clinic',
      subtitle: 'Create the clinic, its first admin, and an active subscription',
      stepOf: 'Step {{step}} of {{total}}',
      clinicName: 'Clinic Name',
      legalName: 'Legal Name',
      contactEmail: 'Contact Email',
      contactPhone: 'Contact Phone',
      line1: 'Address Line 1',
      city: 'City',
      state: 'State',
      postalCode: 'Postal Code',
      country: 'Country (ISO 3166-1 alpha-2)',
      adminName: 'First Admin Name',
      adminEmail: 'First Admin Email',
      planSlug: 'Plan',
      trialDays: 'Trial Days (optional)',
      back: 'Back',
      next: 'Next',
      submit: 'Onboard Clinic',
    },
  },
  banner: {
    impersonation:
      '⚠ You are viewing {{clinic}} as {{staff}} — all actions are audit-logged. Session ends in {{remaining}}.',
    readOnly: 'Account is in read-only mode — renew your subscription to continue.',
  },
} as const;
